import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, Clock, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { formatInt } from "@/lib/format";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";

export interface GmailMailbox {
  mailbox: string;
  daily_through?: string;
  daily_through_mt?: string;
  daily_updated_mt?: string;
  backfill_back_to?: string;
  backfill_done: boolean;
  logged_last_7_days: number;
  logged_all_time: number;
  last_note?: string;
  newest_logged_message_mt?: string;
}

export interface GmailStatus {
  mailboxes: GmailMailbox[];
  internal_domains: string[];
  max_recipients: number;
  sync_requested?: string;
  sync_requested_mt?: string;
  sync_waiting: boolean;
}

interface SyncRequestOut {
  requested_at: string;
  requested_at_mt: string;
  message: string;
}

export function useGmailStatus() {
  return useQuery({ queryKey: ["gmail-status"], queryFn: () => api<GmailStatus>("/api/v1/gmail/status"), refetchInterval: 60_000, retry: false });
}

/** Email → Gmail log: Brandon's one-to-one Gmail on contacts' timelines, and the Sync Gmail now button (gmail_sync_request). */
export function GmailLogPage() {
  const q = useGmailStatus();
  const qc = useQueryClient();
  const toast = useToast();
  const sync = useMutation({
    mutationFn: () => api<SyncRequestOut>("/api/v1/gmail/sync-request", { method: "POST", json: {} }),
    onSuccess: (r) => {
      toast(`Requested at ${r.requested_at_mt}. The mailbox was not read.`);
      void qc.invalidateQueries({ queryKey: ["gmail-status"] });
    },
    onError: (e) => toast(e.message, "error"),
  });
  const s = q.data;
  return (
    <div className="mx-auto max-w-[1100px] space-y-3">
      <PageHeader
        title="Gmail log"
        sub="One-to-one mail on contacts' timelines"
        actions={
          <Button onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw /> Sync Gmail now
          </Button>
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          You keep sending from Spark. The button asks the email check to log one-to-one mail from brandon@creuniversity.com onto people already in the CRM. It does not
          read the mailbox, send, draft or move mail, and it never creates a contact.
        </p>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          The email check logs at 7:00 AM, 11:00 AM, 2:00 PM and 5:00 PM Mountain Time; 11:00 and 5:00 only log mail. The Gmail step is not in the email desk yet, so until
          it is, the button only saves the request.
        </p>
      </PageHeader>
      {q.isLoading && <Skeleton className="h-40" />}
      {q.error && <Empty title="The Gmail log status isn't available on this server.">{q.error.message}</Empty>}
      {s && <Request s={s} />}
      {s && <Mailboxes s={s} />}
    </div>
  );
}

function Request({ s }: { s: GmailStatus }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Last request</CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        {!s.sync_requested_mt ? (
          <p className="text-[var(--color-muted-foreground)]">Nobody has pressed Sync Gmail now yet.</p>
        ) : s.sync_waiting ? (
          <p className="flex items-center gap-2">
            <Clock className="size-4 text-[var(--color-warning-fg)]" />
            Asked {s.sync_requested_mt}. Waiting for the email check to log.
          </p>
        ) : (
          <p className="flex items-center gap-2">
            <CircleCheck className="size-4 text-[var(--color-success-fg)]" />
            Asked {s.sync_requested_mt}. Logged since.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Mailboxes({ s }: { s: GmailStatus }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Mailboxes</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {s.mailboxes.length === 0 ? (
          <Empty title="Nothing logged yet.">The email check hasn't logged any mail yet.</Empty>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Mailbox</TH>
                <TH>Logged up to</TH>
                <TH className="text-right">Last 7 days</TH>
                <TH className="text-right">All time</TH>
                <TH>Older mail</TH>
              </TR>
            </THead>
            <tbody>
              {s.mailboxes.map((m) => (
                <TR key={m.mailbox}>
                  <TD className="font-medium">{m.mailbox}</TD>
                  <TD>{m.daily_through_mt ?? <span className="text-[var(--color-muted-foreground)]">not yet</span>}</TD>
                  <TD className="text-right tabular-nums">{formatInt(m.logged_last_7_days)}</TD>
                  <TD className="text-right tabular-nums">{formatInt(m.logged_all_time)}</TD>
                  <TD>{m.backfill_done ? <Badge variant="success">done</Badge> : m.backfill_back_to ? <Badge variant="muted">in progress</Badge> : <Badge variant="outline">not started</Badge>}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
